import { isObject } from "lodash";

// Simple snake_case implementation to compliment camlize.ts
function snakeCase(str: string): string {
  return str.replace(/([A-Z])/g, "_$1").toLowerCase();
}

// Reverse key transforms - maps camelCase back to snake_case
const reverseKeyTransforms = {
  id: "uuid",
  sectionId: "section_uuid",
  kitId: "kit_uuid",
  itemId: "item_uuid",
  portalId: "portal_uuid",
  assetId: "asset_uuid",
  sourceId: "source_uuid",
} as const;

// Convert camelCase string to snake_case
type CamelToSnake<S extends string> = S extends `${infer Head}${infer Tail}`
  ? Head extends Uppercase<Head>
    ? `_${Lowercase<Head>}${CamelToSnake<Tail>}`
    : `${Head}${CamelToSnake<Tail>}`
  : S;

// Apply reverse key transforms, otherwise convert to snake_case
type ReverseConvert<Key> = Key extends keyof typeof reverseKeyTransforms
  ? (typeof reverseKeyTransforms)[Key]
  : Key extends string
    ? CamelToSnake<Key>
    : Key;

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
type Snakeified<T> = T extends Function
  ? T
  : T extends Array<infer U>
    ? Array<Snakeified<U>>
    : T extends Set<infer U>
      ? Set<Snakeified<U>>
      : T extends object
        ? {
            [Key in keyof T as ReverseConvert<Key>]: Snakeified<T[Key]>;
          }
        : T;
const ignoreKeys = ["upgradePermissions"];

/**
 * Converts an object's keys from camelCase to snake_case recursively.
 * This is the reverse operation of the camelize function.
 *
 * @param obj - The object to convert
 * @returns A new object with snake_case keys
 *
 * @example
 * ```ts
 * const camelCased = { userId: 123, userProfile: { firstName: 'John' } };
 * const snakeCased = snakeify(camelCased);
 * // Result: { user_id: 123, user_profile: { first_name: 'John' } }
 * ```
 */
function snakeify<T extends object>(obj: T): Snakeified<T> {
  if (obj === null || obj === undefined) return obj as Snakeified<T>;

  return Object.keys(obj).reduce((acc, key) => {
    // Check if we should ignore this key before converting it
    if (ignoreKeys.includes(key)) {
      acc[key] = obj[key as keyof T];
      return acc;
    }

    let _key = key;
    if (typeof key === "string") {
      const snakeKey = snakeCase(key);
      _key = reverseKeyTransforms[key as keyof typeof reverseKeyTransforms] || snakeKey;
    }

    const value = obj[key as keyof T];
    if (Array.isArray(value)) {
      acc[_key] = value.map(item => (isObject(item) ? snakeify(item) : item));
    } else if (isObject(value)) {
      acc[_key] = snakeify(value);
    } else {
      acc[_key] = value;
    }
    return acc;
  }, {} as Snakeified<T>);
}
export default snakeify;
export type { Snakeified };
